import { useCallback, useEffect, useState } from "react";
import { Inbox, Loader2, Trash2 } from "lucide-react";
import { listDecodeRequests, updateDecodeRequest, deleteDecodeRequest } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: { label: "NEW", cls: "text-[var(--primary)] border-[rgba(212,175,55,0.5)]" },
  in_progress: { label: "IN PROGRESS", cls: "text-white border-white/30" },
  done: { label: "DONE", cls: "border-[rgba(16,185,129,0.5)]", style: { color: "#6EE7B7" } },
  refunded: { label: "REFUNDED ⚡", cls: "border-[rgba(239,68,68,0.5)]", style: { color: "#F87171" } },
};

export default function RequestsInbox() {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDecodeRequests();
      setItems(r.items || []);
    } catch {
      setItems(null); // token invalid — hide inbox
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    if (status === "refunded" && !window.confirm("Mark refunded? This adds a STRIKE against their email — 3 strikes blocks them for good.")) return;
    try {
      await updateDecodeRequest(id, status);
      toast.success(`Marked ${status.replace("_", " ")}`);
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request? (Deleting a refunded one also removes its strike.)")) return;
    try {
      await deleteDecodeRequest(id);
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (items === null && !loading) return null;

  return (
    <div className="rise rise-3 panel p-6 md:p-8 border-[rgba(212,175,55,0.25)]" data-testid="requests-inbox">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Inbox className="gold" size={16} />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] gold">
            // Admin — Decode Requests {items && `(${items.length})`}
          </span>
        </div>
        <button onClick={load} className="font-mono text-[10px] uppercase tracking-wider muted hover:text-white">
          ↻ Refresh
        </button>
      </div>

      {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin muted" size={18} /></div>}

      {!loading && items && items.length === 0 && (
        <div className="muted font-mono text-sm text-center py-6">No requests yet.</div>
      )}

      {!loading && items && items.map((r) => {
        const st = STATUS_STYLES[r.status] || STATUS_STYLES.new;
        return (
          <div key={r.id} className="border border-white/10 bg-black/20 p-4 mb-3 last:mb-0" data-testid={`request-item-${r.id}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <span className={`font-mono text-[9px] uppercase tracking-wider px-2 py-0.5 border ${st.cls}`} style={st.style}>
                {st.label}
              </span>
              <span className="font-display text-base">{r.name}</span>
              <a href={`mailto:${r.email}?subject=Your Gem Key decode request`} className="font-mono text-xs gold hover:underline">
                {r.email}
              </a>
              {r.strikes > 0 && (
                <span className="font-mono text-[9px] px-2 py-0.5 border border-[rgba(239,68,68,0.5)]" style={{ color: "#F87171" }} data-testid={`request-strikes-${r.id}`}>
                  {r.strikes}/3 STRIKES{r.strikes >= 3 ? " — BLOCKED" : ""}
                </span>
              )}
              <span className="font-mono text-[10px] muted ml-auto">
                {new Date(r.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              </span>
            </div>
            <p className="text-sm font-mono muted leading-relaxed whitespace-pre-wrap mb-3">{r.request_text}</p>
            <div className="flex flex-wrap gap-2">
              {r.status !== "in_progress" && (
                <button onClick={() => setStatus(r.id, "in_progress")} className="btn-secondary px-3 py-1.5 text-[10px]" data-testid={`request-progress-${r.id}`}>Working on it</button>
              )}
              {r.status !== "done" && (
                <button onClick={() => setStatus(r.id, "done")} className="btn-secondary px-3 py-1.5 text-[10px]" data-testid={`request-done-${r.id}`}>Done ✓</button>
              )}
              {r.status !== "refunded" && (
                <button onClick={() => setStatus(r.id, "refunded")} className="btn-secondary px-3 py-1.5 text-[10px]" style={{ color: "#F87171" }} data-testid={`request-refund-${r.id}`}>Refund + strike</button>
              )}
              <button onClick={() => remove(r.id)} className="ml-auto muted hover:text-white" title="Delete request" data-testid={`request-delete-${r.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
